import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../Header';

import { listReviewTypes, listPerformanceRatings } from '../../api/reviews';
import { listManagedKpiReviewsForEmployee, updateManagerReview } from '../../api/reviewsManaged';
import { upsertReview as upsertKpiReview } from '../../api/reviewsUpsert';
import { fetchKpaReviews, updateKpaManagerComment } from '../../api/kpaReviews';
import { listKpas } from '../../api/kpas';

import KPIReviewRowManager from '../../components/reviews/KPIReviewRowManager';
import KPAReviewSummaryTable from '../../components/reviews/KPAReviewSummaryTable';

// Company Value Reviews (Manager)
import {
    listManagedCompanyValueReviews,
    updateCompanyValueReviewManager,
} from '../../api/companyValueReviews';
import CompanyValuesTableManager from '../../components/reviews/CompanyValuesTableManager';

import '../../components/reviews/reviews-table.css';
import CompanyValuesSummaryTable from "../../components/reviews/CompanyValuesSummaryTable";
import GeneralAssessment from "../../components/reviews/GeneralAssessment";
import {
    fetchManagedGeneralAssessment,
    updateGeneralAssessmentManager,
} from "../../api/generalAssessments";

//Overall rating
import { getOverallRating, getOverallEligibility, updateOverallManager, acceptOverall } from '../../api/overallRatings';
import OverallRatingCard from '../../components/reviews/OverallRatingCard';


// Group managed KPI result rows by KPA and sort strictly by numeric KPA id
function groupByKpa(items) {
    const map = new Map();
    for (const it of items || []) {
        const kpaId = it.kpi?.kpa?.id ?? it.kpi?.key_performance_area_id ?? null;
        const kpaName = it.kpi?.kpa?.name ?? (kpaId ? `KPA ${kpaId}` : 'Ungrouped');
        const kpaWeight = it.kpi?.kpa?.weight ?? null;
        if (!map.has(kpaId)) {
            map.set(kpaId, { kpaId, kpaName, kpaWeight, rows: [] });
        }
        map.get(kpaId).rows.push(it);
    }
    // Strict numeric order by kpaId; nulls last
    return Array.from(map.values()).sort((a, b) => {
        const ai = a.kpaId == null ? Number.MAX_SAFE_INTEGER : Number(a.kpaId);
        const bi = b.kpaId == null ? Number.MAX_SAFE_INTEGER : Number(b.kpaId);
        return ai - bi;
    });
}

export default function TeamPerformanceReviewsEmployee() {

    const [overall, setOverall] = useState(null);
    const [overallElig, setOverallElig] = useState({ ok: false, errors: [] });
    const refreshOverall = async (contractId, rtId = reviewTypeId) => {
        const cId = contractId ?? employeeContractId;
        if (!cId || !String(rtId)) return;
        const res = await getOverallRating(cId, rtId);
        setOverall(res?.overall || null);
        const elig = await getOverallEligibility(cId);
        setOverallElig(elig);
    };

    const { employeeId } = useParams();

    const [reviewTypes, setReviewTypes] = useState([]);
    const [reviewTypeId, setReviewTypeId] = useState('');
    const [ratings, setRatings] = useState([]);

    const [rows, setRows] = useState([]);
    const [kpaSummary, setKpaSummary] = useState([]);
    const [employeeContractId, setEmployeeContractId] = useState(null);

    const [cvRows, setCvRows] = useState([]);
    const [generalSummary, setGeneralSummary] = useState(null);

    // collapsible per KPA
    const [openKpas, setOpenKpas] = useState(new Set());
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);

    const sections = useMemo(() => groupByKpa(rows), [rows]);

    const isOpen = (kpaId) => openKpas.has(String(kpaId));
    const toggle = (kpaId) =>
        setOpenKpas((prev) => {
            const s = new Set(prev);
            const k = String(kpaId);
            s.has(k) ? s.delete(k) : s.add(k);
            return s;
        });
    const expandAll = () => setOpenKpas(new Set(sections.map((s) => String(s.kpaId))));
    const collapseAll = () => setOpenKpas(new Set());

    // Init lookups
    useEffect(() => {
        (async () => {
            try {
                const [rt, pr] = await Promise.all([listReviewTypes(), listPerformanceRatings()]);
                setReviewTypes(rt || []);
                setRatings(pr || []);
                const defId = rt?.[0]?.id ? String(rt[0].id) : '';
                setReviewTypeId(defId);
            } catch {
                setErr('Failed to initialize review types/ratings.');
            }
        })();
    }, []);

    // Load employee KPI rows for manager
    useEffect(() => {
        if (!reviewTypeId || !employeeId) return;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const res = await listManagedKpiReviewsForEmployee(reviewTypeId, employeeId);
                setRows(res.data || []);
                const secs = groupByKpa(res.data || []);
                setOpenKpas(new Set(secs.map((s) => String(s.kpaId))));
            } catch (e) {
                setErr(e?.response?.data?.message || 'Failed to load employee KPI reviews.');
            } finally {
                setLoading(false);
            }
        })();
    }, [reviewTypeId, employeeId]);

    // Load KPA summaries for employee (manager view) — also capture the employee's contract id
    useEffect(() => {
        if (!reviewTypeId || !employeeId) return;
        (async () => {
            try {
                const res = await fetchKpaReviews({ reviewTypeId, employeeId });
                setKpaSummary(res?.data || []);
                setEmployeeContractId(res?.contract?.id ?? null);
            } catch (e) {
                console.warn('Failed to load KPA summaries (manager)', e?.response?.data || e);
            }
        })();
    }, [reviewTypeId, employeeId]);

    // Load Company Value reviews (manager view)
    useEffect(() => {
        if (!reviewTypeId || !employeeId) return;
        (async () => {
            try {
                const res = await listManagedCompanyValueReviews(reviewTypeId, employeeId);
                setCvRows(res?.data || []);
            } catch (e) {
                console.warn('Failed to load Company Value reviews (manager)', e?.response?.data || e);
            }
        })();
    }, [reviewTypeId, employeeId]);

    // Load General Assessment summary (manager view)
    useEffect(() => {
        if (!reviewTypeId || !employeeId) return;
        (async () => {
            try {
                const res = await fetchManagedGeneralAssessment(reviewTypeId, employeeId);
                setGeneralSummary(res?.summary || null);
                setEmployeeContractId(res?.contract?.id ?? null);
            } catch (e) {
                console.warn('Failed to load General Assessment (manager)', e?.response?.data || e);
            }
        })();
    }, [reviewTypeId, employeeId]);

    // Manager saves KPI row (create/update)
    const onSaveManagerRow = async ({ reviewId, kpiId, payload }) => {
        let id = reviewId;
        if (!id) {
            const created = await upsertKpiReview({
                reviewId: null,
                reviewTypeId,
                kpiId,
                body: payload,
            });
            id = created?.id ?? created?.review?.id ?? created?.data?.id ?? null;
        }
        if (!id) throw new Error('Unable to create review for manager update.');
        await updateManagerReview(id, payload);
        const [kpiRes, kpaRes, cvRes] = await Promise.all([
            listManagedKpiReviewsForEmployee(reviewTypeId, employeeId),
            fetchKpaReviews({ reviewTypeId, employeeId }),
            listManagedCompanyValueReviews(reviewTypeId, employeeId),
        ]);
        setRows(kpiRes.data || []);
        setKpaSummary(kpaRes?.data || []);
        setEmployeeContractId(kpaRes?.contract?.id ?? null);
        setCvRows(cvRes?.data || []);
        await refreshOverall(kpaRes?.contract?.id ?? employeeContractId, reviewTypeId);
    };

    // Manager saves KPA manager_comment
    const onSaveKpaManager = async ({ id, value }) => {
        await updateKpaManagerComment(id, value);
        const res = await fetchKpaReviews({ reviewTypeId, employeeId });
        setKpaSummary(res?.data || []);
        setEmployeeContractId(res?.contract?.id ?? null);
    };

    // Manager saves Company Value (manager fields)
    const onSaveCompanyValueManager = async ({ reviewId, payload }) => {
        await updateCompanyValueReviewManager(reviewId, payload);
        const res = await listManagedCompanyValueReviews(reviewTypeId, employeeId);
        setCvRows(res?.data || []);
    };

    // Manager saves Company Value comment in the summary table (manager_comment)
    const onSaveCompanyValueComment = async ({ id, value }) => {
        // `id` is the company_value_review id (same as used in row updates)
        await updateCompanyValueReviewManager(id, { manager_comment: value });
        // Refresh the manager's company value reviews for this employee & review type
        const res = await listManagedCompanyValueReviews(reviewTypeId, employeeId);
        setCvRows(res?.data || []);
    };

    const onSaveGeneralAssessment = async ({ id, field, value }) => {
        if (!id) return;
        await updateGeneralAssessmentManager(id, { [field]: value });
        const res = await fetchManagedGeneralAssessment(reviewTypeId, employeeId);
        setGeneralSummary(res?.summary || null);
        setEmployeeContractId(res?.contract?.id ?? null);
    };

    // Provide rating code for modal fields
    const getRatingLabel = (id) => {
        if (!id) return '';
        const r = ratings.find((x) => String(x.id) === String(id));
        return r?.code || '';
    };

    // Load extra KPA details for modal (name, description, weight, timeframe, alignment)
    const loadKpaDetails = async (kpaId) => {
        if (!employeeContractId) return null;
        try {
            const list = await listKpas(employeeContractId);
            return list.find((k) => Number(k.id) === Number(kpaId)) || null;
        } catch {
            return null;
        }
    };

    //Loader (when employeeContractId + reviewTypeId ready)
    useEffect(() => {
        (async () => {
            await refreshOverall(employeeContractId, reviewTypeId);
        })();
    }, [employeeContractId, reviewTypeId]);

    const saveManagerOverall = async ({ rating_id, description }) => {
        if (!overall?.id) return;
        await updateOverallManager(overall.id, {
            manager_rating_id: rating_id,
            manager_description: description,
        });
        await refreshOverall();
    };

    const acceptAsManager = async () => {
        if (!overall?.id) return;
        await acceptOverall(overall.id);
        await refreshOverall();
    };

    return (
        <>
            <Header label={<p className="py-0 my-0">Team Performance Reviews</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header d-flex align-items-center gap-3">
                                <Link to="/reviews/team" className="btn btn-sm btn-secondary">
                                    ← Back
                                </Link>

                                <h5 className="mb-0">Review Type</h5>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ maxWidth: 260 }}
                                    value={reviewTypeId}
                                    onChange={(e) => setReviewTypeId(e.target.value)}
                                >
                                    {reviewTypes.map((rt) => (
                                        <option key={rt.id} value={rt.id}>
                                            {rt.name}
                                        </option>
                                    ))}
                                </select>

                                <div className="ms-auto d-flex gap-2">
                                    <button className="btn btn-sm btn-secondary" onClick={expandAll}>
                                        Expand all
                                    </button>
                                    <button className="btn btn-sm btn-tertiary" onClick={collapseAll}>
                                        Collapse all
                                    </button>
                                </div>
                            </div>

                            <div className="card-body">
                                {err && <div className="alert alert-danger">{err}</div>}

                                {/* KPI Table */}
                                <div className="reviews-table">
                                    <table>
                                        <thead>
                                        <tr className="rt-head-top">
                                            <th rowSpan={2} className="rt-counter">
                                                #
                                            </th>
                                            <th rowSpan={2} className="rt-name">
                                                Performance Review Discussion and Ratings
                                            </th>
                                            <th colSpan={3}>Acceptable / Expected / Exceeded Output</th>
                                            <th rowSpan={2}>Weight</th>
                                            <th rowSpan={2}>Actuals</th>
                                            <th colSpan={2}>Employee Evaluation</th>
                                            <th colSpan={2}>Employer Evaluation</th>
                                            <th rowSpan={2} className="rt-actions">
                                                Actions
                                            </th>
                                        </tr>
                                        <tr className="rt-head-bottom">
                                            <th>Threshold</th>
                                            <th>Target</th>
                                            <th>Stretch</th>
                                            <th>Rating (EE)</th>
                                            <th>Description</th>
                                            <th>Rating (ER)</th>
                                            <th>Description</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {sections.length === 0 && (
                                            <tr>
                                                <td colSpan={12} className="text-center text-muted py-4">
                                                    {loading ? 'Loading…' : 'No KPIs found.'}
                                                </td>
                                            </tr>
                                        )}

                                        {sections.map((sec, kpaIdx) => {
                                            const open = isOpen(sec.kpaId);
                                            const kpaCounter = `KPA ${String(kpaIdx + 1).padStart(2, '0')}`;

                                            return (
                                                <React.Fragment key={String(sec.kpaId)}>
                                                    <tr className="rt-kpa-row">
                                                        <td className="rt-counter">
                                                            <button className="rt-kpa-toggle" onClick={() => toggle(sec.kpaId)}>
                                                                <span className="me-2">{open ? '▾' : '▸'}</span>
                                                                {kpaCounter}
                                                            </button>
                                                        </td>
                                                        <td className="rt-name">{sec.kpaName}</td>
                                                        <td className="rt-num">—</td>
                                                        <td className="rt-num">—</td>
                                                        <td className="rt-num">—</td>
                                                        <td className="rt-num">{sec.kpaWeight ?? '—'}</td>
                                                        <td className="rt-num">—</td>
                                                        <td colSpan={4}></td>
                                                        <td className="rt-actions"></td>
                                                    </tr>

                                                    {open &&
                                                        sec.rows.map((row, kpiIdx) => (
                                                            <KPIReviewRowManager
                                                                key={row.kpi.id}
                                                                kpi={row.kpi}
                                                                review={row.review}
                                                                ratings={ratings}
                                                                onSave={onSaveManagerRow}
                                                                counterText={`KPI ${String(kpiIdx + 1).padStart(2, '0')}`}
                                                            />
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* KPA Summary (Manager view) */}
                                <KPAReviewSummaryTable
                                    items={kpaSummary}
                                    role="manager"
                                    onSaveComment={onSaveKpaManager}
                                    getRatingLabel={getRatingLabel}
                                    loadKpaDetails={loadKpaDetails}
                                    contractId={employeeContractId}
                                />

                                {/* Company Value Reviews (Manager) */}
                                <h6 className="mt-4 mb-2">Company Values</h6>
                                <CompanyValuesTableManager
                                    rows={cvRows}
                                    ratings={ratings}
                                    onSaveRow={onSaveCompanyValueManager}
                                />
                                {/* Company Values Summary (NEW) */}
                                <CompanyValuesSummaryTable
                                    items={cvRows}               // [{ company_value:{...}, review:{...} }]
                                    role="manager"              // this page is the manager view
                                    onSaveComment={onSaveCompanyValueComment}
                                    // Optional enrichment for the modal (not required):
                                    // loadValueDetails={(valueId) => api.get(`/api/company-values/${valueId}`).then(r => r.data)}
                                />

                                {/* General Assessment */}
                                <GeneralAssessment
                                    summary={generalSummary}
                                    role="manager"
                                    onSaveComment={onSaveGeneralAssessment}
                                />

                                {overall && (
                                    <OverallRatingCard
                                        overall={overall}
                                        reviewTypeName={reviewTypes.find(rt => String(rt.id) === String(reviewTypeId))?.name ?? ''}
                                        role="manager"
                                        ratings={ratings}
                                        eligibility={overallElig}
                                        onSaveEmployee={async () => {}}  // noop
                                        onSaveManager={saveManagerOverall}
                                        onAccept={acceptAsManager}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
